import { isEmpty } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { TouchableOpacity } from 'react-native';
import { AnchoredSheet, AnchoredSheetTitleBar } from '@shared/components/AnchoredSheet';
import { MBButton } from '@shared/components/MBButton';
import { Menu } from '@shared/components/Menu';
import { useToast } from '@shared/components/Toast';
import { COPY_TOOLTIP_DURATION_MS } from '@shared/components/UrlActionButtons';
import { View } from '@shared/components/View';
import { MBTextInput } from '@shared/components/textInput';
import { TruncateLink } from '@shared/components/truncate';
import { UrlField } from '@shared/data/entity/fields/UrlField';
import {
  getComponentLabelWithFallback,
  getComponentPlaceholder,
} from '@shared/scenes/workflows/utils';
import { Colors, getAnchorName, grid } from '@shared/styles';
import { copyToClipboard } from '@shared/util/clipboard';
import { useBoolean, useMountedRef } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { openLink } from '@shared/util/link';
import { addPrefixIfNeeded, validateUrl } from '@shared/util/validate/url';
import { EditFieldContainer } from '../EditFieldContainer';
import { UrlEditFieldProps } from './UrlEditFieldProps';

export function UrlEditFieldMobile({
  field,
  component,
  updateField,
  error,
  allowClear,
  style,
}: UrlEditFieldProps) {
  const [value, setValue] = useState(field?.url ?? '');
  const hasValue = !isEmpty(value);
  const isReadOnly = !!component.readonly;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isModalOpen, openModal, closeModal] = useBoolean();
  const modalTitle = getComponentLabelWithFallback(component);
  const anchorName = getAnchorName('url-edit-field', component.id);
  const toast = useToast();
  const href = addPrefixIfNeeded(value);

  const openMenu = useCallback(() => {
    if (hasValue) {
      setIsMenuOpen(true);
    }
  }, [hasValue]);

  const openInBrowser = useCallback(() => {
    openLink(href);
  }, [href]);

  const copyLink = useCallback(() => {
    void copyToClipboard(href);
    toast({
      message: i18n('workflowTemplate.component.url.actionButtonsSheet.copiedToClipboard'),
    });
  }, [href, toast]);

  useEffect(() => {
    const updatedField = !isEmpty(value) ? new UrlField([{ url: value.trim() }]) : null;
    updateField(component.id, updatedField);
  }, [value, updateField, component.id]);

  useEffect(() => {
    if (!isModalOpen) {
      setValue(addPrefixIfNeeded);
    }
  }, [isModalOpen]);

  const onClear = useCallback(() => {
    setValue('');
  }, []);

  const showClearButton = hasValue && !isReadOnly && allowClear;
  const placeholder = getComponentPlaceholder(component) ?? '';
  const previewText = value || placeholder;
  const placeholderStyle = { color: Colors.v2.gray50 };

  return (
    <EditFieldContainer
      component={component}
      showLabel={true}
      error={error}
      showClearButton={showClearButton}
      onClear={onClear}
      style={style}
      anchorName={anchorName}
      printContent={field?.url || undefined}
    >
      <TouchableOpacity onPress={isReadOnly ? openMenu : openModal} onLongPress={openMenu}>
        <View paddingRight={2}>
          <TruncateLink
            href={previewText}
            showProtocol={true}
            style={!hasValue ? placeholderStyle : undefined}
          />
        </View>
        {isModalOpen && (
          <UrlEditSheet
            value={value}
            setValue={setValue}
            closeModal={closeModal}
            modalTitle={modalTitle}
            placeholder={placeholder}
            anchorName={anchorName}
          />
        )}
      </TouchableOpacity>
      <Menu isOpen={isMenuOpen} onOpenChange={setIsMenuOpen} anchorName={anchorName}>
        <Menu.Content>
          <Menu.Note icon={false}>{value}</Menu.Note>
          <Menu.Item
            label={i18n('workflowTemplate.component.url.actionButtonsSheet.openInBrowser')}
            onClick={openInBrowser}
          />
          <Menu.Item
            label={i18n('workflowTemplate.component.url.actionButtonsSheet.copyLink')}
            onClick={copyLink}
          />
        </Menu.Content>
      </Menu>
    </EditFieldContainer>
  );
}

function UrlEditSheet({
  value,
  setValue,
  closeModal,
  modalTitle,
  placeholder,
  anchorName,
}: {
  value: string;
  setValue: (value: string) => void;
  closeModal: VoidFunction;
  modalTitle: string;
  placeholder?: string;
  anchorName: string;
}) {
  return (
    <AnchoredSheet
      onClose={closeModal}
      anchorName={anchorName}
      renderTitleBar={() => {
        return (
          <AnchoredSheetTitleBar
            onRightIconPress={closeModal}
            title={modalTitle}
            rightIcon="exit"
            rightIconColor={Colors.v2.purple}
            rightIconSize={grid(4)}
          />
        );
      }}
    >
      <View padding={2}>
        <MBTextInput
          multiline={true}
          inputMode="url"
          textContentType="URL"
          keyboardType="url"
          value={value}
          onChangeText={setValue}
          placeholder={placeholder}
          autoCapitalize="none"
          autoFocus={true}
          style={{
            fontSize: 16,
            lineHeight: 24,
            backgroundColor: Colors.v2.gray05,
            maxHeight: 250,
          }}
        />
        <UrlEditSheetButtons value={value} />
      </View>
    </AnchoredSheet>
  );
}

function UrlEditSheetButtons({ value }: { value: string }) {
  const mounted = useMountedRef();
  const openLinkLabel = i18n('workflowTemplate.component.url.actionButtonsSheet.openInBrowser');
  const copyLinkLabel = i18n('workflowTemplate.component.url.actionButtons.copyToClipboard');
  const copiedLinkLabel = i18n('workflowTemplate.component.url.actionButtons.copiedToClipboard');
  const [copyButtonLabel, setCopyButtonLabel] = useState(copyLinkLabel);
  const href = addPrefixIfNeeded(value);
  const isValid = validateUrl(href);

  const onClickCopy = () => {
    void copyToClipboard(href);
    setCopyButtonLabel(copiedLinkLabel);
    setTimeout(() => {
      if (mounted.current) {
        setCopyButtonLabel(copyLinkLabel);
      }
    }, COPY_TOOLTIP_DURATION_MS);
  };

  const openUrl = () => {
    openLink(href);
  };

  const opacity = isValid ? 1 : 0.3;

  return (
    <View row paddingTop={3} style={{ justifyContent: 'space-between', opacity }}>
      <View flex style={{ flexBasis: '55%' }}>
        <MBButton
          leftIcon="link"
          leftIconSize={grid(4)}
          backgroundColor={Colors.v2.gray05}
          titleColor={Colors.v2.purple}
          title={openLinkLabel}
          onPress={isValid ? openUrl : undefined}
          style={{ flex: 1, paddingLeft: grid(1) }}
        />
      </View>
      <View flex style={{ flexBasis: '5%' }} />
      <View flex style={{ flexBasis: '40%' }}>
        <MBButton
          leftIcon="squares-stacked"
          leftIconSize={grid(4)}
          backgroundColor={Colors.v2.gray05}
          titleColor={Colors.v2.purple}
          title={copyButtonLabel}
          onPress={isValid ? onClickCopy : undefined}
          style={{ flex: 1, paddingLeft: grid(1) }}
        />
      </View>
    </View>
  );
}
